import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import API from '../utils/api';
import './Exam.css';

const FACE_CHECK_INTERVAL = 2000;
const FACE_MISSING_THRESHOLD = 5000;

export default function Exam() {
  const { session_id } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [disqualified, setDisqualified] = useState(false);
  const [violationCount, setViolationCount] = useState(0);
  const [maxViolations, setMaxViolations] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [faceStatus, setFaceStatus] = useState('detecting');
  const [cameraError, setCameraError] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningDetail, setWarningDetail] = useState('');
  const [facePhotoSaved, setFacePhotoSaved] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [rulesConfirmed, setRulesConfirmed] = useState(false);

  const phoneViolationRef = useRef(false);
  const webcamRef = useRef(null);
  // objectModelRef dinonaktifkan — model COCO-SSD tidak dimuat
  // const objectModelRef = useRef(null);
  // const objectTimerRef = useRef(null);
  const canvasRef = useRef(null);
  const faceTimerRef = useRef(null);
  const faceMissingStartRef = useRef(null);
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const violationLockRef = useRef(false);
  const fullscreenReadyRef = useRef(false);
  const fullscreenRequestRef = useRef(false);
  const hasEverEnteredFullscreenRef = useRef(false);

  const isMobileDevice = useMemo(() => {
    const ua = navigator.userAgent || '';
    const mobileUA = /Android|iPhone|iPad|iPod|Windows Phone|Mobile/i.test(ua);
    const coarsePointer = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
    const smallScreen = typeof window !== 'undefined' ? window.innerWidth <= 1024 : false;
    return mobileUA || (coarsePointer && smallScreen);
  }, []);
  const requireFullscreen = !isMobileDevice;
  const webcamVideoConstraints = useMemo(
    () => ({
      facingMode: { ideal: 'user' },
      width: { ideal: 640 },
      height: { ideal: 480 },
    }),
    [isMobileDevice]
  );

  const handleCameraError = useCallback(() => {
    setCameraError(true);
    setError(
      isMobileDevice
        ? 'Kamera depan wajib digunakan. Pastikan izin kamera diberikan dan pilih kamera depan pada perangkat Anda.'
        : 'Kamera tidak tersedia. Pastikan izin kamera sudah diberikan.'
    );
  }, [isMobileDevice]);

  const handleUserMedia = useCallback((stream) => {
    if (!isMobileDevice) {
      setCameraError(false);
      return;
    }

    const track = stream?.getVideoTracks?.()[0];
    const settings = track?.getSettings ? track.getSettings() : {};
    const facingMode = String(settings?.facingMode || '').toLowerCase();
    const label = String(track?.label || '').toLowerCase();
    const looksBackCamera =
      facingMode === 'environment' ||
      label.includes('back') ||
      label.includes('rear') ||
      label.includes('environment');

    if (looksBackCamera) {
      stream.getTracks().forEach((t) => t.stop());
      setCameraError(true);
      setError('Kamera depan wajib digunakan. Silakan pilih front camera pada browser iPhone Anda.');
      return;
    }

    setCameraError(false);
    setError((prev) => (prev && prev.includes('Kamera depan wajib') ? '' : prev));
  }, [isMobileDevice]);

  // ─── Load exam ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.get(`/exam/session/${session_id}`);
        const data = res.data;
        if (data.session.status === 'disqualified') {
          setDisqualified(true);
          setLoading(false);
          return;
        }
        setSession(data.session);
        setQuestions(data.questions);
        setRemainingSeconds(data.remainingSeconds);
        setExamStarted(!!data.session.exam_started);
        setMaxViolations(data.session.max_violations || 1);
        setViolationCount(data.session.violation_count || 0);
        const savedMap = {};
        data.questions.forEach(q => { if (q.savedAnswer) savedMap[q.id] = q.savedAnswer; });
        setAnswers(savedMap);
      } catch (err) {
        setError(err.response?.data?.message || 'Gagal memuat ujian');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [session_id]);

  // ─── Load AI model — DINONAKTIFKAN (terlalu berat) ──────────────────────────
  // useEffect(() => {
  //   const loadModel = async () => {
  //     try {
  //       console.log('Loading AI model...');
  //       objectModelRef.current = await window.cocoSsd.load();
  //       console.log('AI model loaded');
  //     } catch (err) {
  //       console.error('Model load error', err);
  //     }
  //   };
  //   loadModel();
  // }, []);

  // ─── Log violation helper ───────────────────────────────────────────────────
  const reportViolation = useCallback(async (type, description, screenshot = null) => {
    if (violationLockRef.current) return;
    violationLockRef.current = true;
    try {
      const res = await API.post('/exam/violation', {
        session_id,
        violation_type: type,
        description,
        screenshot,
      });
      const newCount = res.data.violation_count;
      setViolationCount(newCount);
      if (res.data.disqualified) {
        setDisqualified(true);
        setShowWarningModal(false);
      } else {
        setWarningDetail(description);
        setShowWarningModal(true);
        setTimeout(() => setShowWarningModal(false), 5000);
      }
    } catch (e) {
      console.error('Violation log error', e);
    } finally {
      setTimeout(() => { violationLockRef.current = false; }, 3000);
    }
  }, [session_id]);

  // ─── Save initial face photo ────────────────────────────────────────────────
  const saveFacePhoto = useCallback(async () => {
    if (facePhotoSaved || !webcamRef.current) return;
    try {
      const screenshot = webcamRef.current.getScreenshot();
      if (screenshot) {
        await API.post('/exam/save-face', { session_id, screenshot });
        setFacePhotoSaved(true);
        console.log('Face photo saved');
      }
    } catch (err) {
      console.error('Save face photo error:', err);
    }
  }, [facePhotoSaved, session_id]);

  // ─── Object / phone detection — DINONAKTIFKAN (membutuhkan model AI berat) ──
  // const detectObjects = useCallback(async () => {
  //   try {
  //     if (!objectModelRef.current || !webcamRef.current) return;
  //     const video = webcamRef.current.video;
  //     if (!video || video.readyState !== 4) return;
  //
  //     const predictions = await objectModelRef.current.detect(video);
  //
  //     const phoneDetected = predictions.some(
  //       p => p.class === 'cell phone' && p.score > 0.75
  //     );
  //
  //     if (phoneDetected) {
  //       if (phoneViolationRef.current) return;
  //       phoneViolationRef.current = true;
  //       reportViolation(
  //         'phone_detected',
  //         'Handphone terdeteksi kamera',
  //         webcamRef.current.getScreenshot()
  //       );
  //     } else {
  //       phoneViolationRef.current = false;
  //     }
  //
  //     const persons = predictions.filter(p => p.class === 'person' && p.score > 0.7);
  //     if (persons.length > 1) {
  //       const screenshot = webcamRef.current.getScreenshot();
  //       reportViolation('face_multiple', 'Terdeteksi lebih dari satu orang', screenshot);
  //     }
  //   } catch (err) {
  //     console.error(err);
  //   }
  // }, [reportViolation]);

  // ─── Object detection interval — DINONAKTIFKAN ─────────────────────────────
  // useEffect(() => {
  //   if (!session || disqualified) return;
  //   objectTimerRef.current = setInterval(detectObjects, 3000);
  //   return () => clearInterval(objectTimerRef.current);
  // }, [session, disqualified, detectObjects]);

  // ─── Alt+Tab / Tab Change Detection ────────────────────────────────────────
  useEffect(() => {
    if (!session || disqualified || !examStarted || isMobileDevice) return;

    const handleBlur = () => {
      if (fullscreenRequestRef.current) return;
      reportViolation('window_blur', 'Peserta berpindah aplikasi atau Alt+Tab');
    };

    const handleVisibility = () => {
      if (document.hidden) {
        reportViolation('tab_change', 'Peserta meninggalkan halaman ujian');
      }
    };

    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [session, disqualified, reportViolation, examStarted, isMobileDevice]);

  // ─── Copy-paste prevention ──────────────────────────────────────────────────
  useEffect(() => {
    if (!session || disqualified || !examStarted) return;
    const block = (e) => {
      e.preventDefault();
      reportViolation('copy_attempt', 'Percobaan copy/paste terdeteksi');
    };
    const blockContext = (e) => e.preventDefault();
    document.addEventListener('copy', block);
    document.addEventListener('paste', block);
    document.addEventListener('cut', block);
    document.addEventListener('contextmenu', blockContext);
    return () => {
      document.removeEventListener('copy', block);
      document.removeEventListener('paste', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('contextmenu', blockContext);
    };
  }, [session, disqualified, reportViolation, examStarted]);

  // ─── Fullscreen management ──────────────────────────────────────────────────
  const enterFullscreen = useCallback(async () => {
    try {
      fullscreenRequestRef.current = true;
      const el = document.documentElement;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.mozRequestFullScreen) {
        await el.mozRequestFullScreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      }
      setIsFullscreen(true);
      hasEverEnteredFullscreenRef.current = true;
      setTimeout(() => { fullscreenRequestRef.current = false; }, 2000);
    } catch (err) {
      console.error('Fullscreen gagal:', err);
      setTimeout(() => { fullscreenRequestRef.current = false; }, 2000);
    }
  }, []);

  useEffect(() => {
    const checkFullscreen = () => {
      if (!requireFullscreen) {
        setIsFullscreen(true);
        return;
      }

      const isFull =
        !!document.fullscreenElement ||
        !!document.webkitFullscreenElement ||
        !!document.mozFullScreenElement;

      setIsFullscreen(isFull);

      if (isFull) {
        hasEverEnteredFullscreenRef.current = true;
        fullscreenRequestRef.current = false;
        return;
      }

      if (!hasEverEnteredFullscreenRef.current) return;
      if (fullscreenRequestRef.current) return;

      if (session && !disqualified && examStarted) {
        reportViolation('fullscreen_exit', 'Peserta keluar dari mode fullscreen');
      }
    };

    checkFullscreen();
    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('mozfullscreenchange', checkFullscreen);
    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('mozfullscreenchange', checkFullscreen);
    };
  }, [session, disqualified, reportViolation, examStarted, requireFullscreen]);

  // ─── Timer countdown ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!session || disqualified || !examStarted || remainingSeconds <= 0) return;
    timerRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [session, disqualified, examStarted]); // eslint-disable-line

  // ─── Auto-save answers every 30s ────────────────────────────────────────────
  useEffect(() => {
    if (!session || disqualified || !examStarted) return;
    autoSaveRef.current = setInterval(() => {
      Object.entries(answers).forEach(([qId, opt]) => {
        API.post('/exam/answer', { session_id, question_id: qId, selected_option: opt }).catch(() => {});
      });
    }, 30000);
    return () => clearInterval(autoSaveRef.current);
  }, [session, disqualified, answers, session_id, examStarted]);

  // ─── Face detection via canvas analysis (skin pixel) — tetap aktif ─────────
  const checkFace = useCallback(() => {
    if (!webcamRef.current || !canvasRef.current) return;
    const video = webcamRef.current.video;
    if (!video || video.readyState !== 4) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 160;
    canvas.height = 120;
    ctx.drawImage(video, 0, 0, 160, 120);

    const imgData = ctx.getImageData(0, 0, 160, 120);
    const pixels = imgData.data;
    let skinPixels = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
      if (r > 80 && g > 40 && b > 20 && r > g && r > b && (r - g) > 15 && r < 250) {
        skinPixels++;
      }
    }
    const ratio = skinPixels / (160 * 120);
    const faceDetected = ratio > 0.05;

    if (faceDetected) {
      setFaceStatus('ok');
      faceMissingStartRef.current = null;
      saveFacePhoto();
    } else {
      setFaceStatus('missing');
      if (!faceMissingStartRef.current) {
        faceMissingStartRef.current = Date.now();
      } else if (examStarted && Date.now() - faceMissingStartRef.current >= FACE_MISSING_THRESHOLD) {
        faceMissingStartRef.current = null;
        const screenshot = webcamRef.current.getScreenshot();
        reportViolation('face_missing', 'Wajah tidak terdeteksi selama lebih dari 5 detik', screenshot);
      }
    }
  }, [reportViolation, saveFacePhoto, examStarted]);

  useEffect(() => {
    if (!session || disqualified || cameraError) return;
    faceTimerRef.current = setInterval(checkFace, FACE_CHECK_INTERVAL);
    return () => clearInterval(faceTimerRef.current);
  }, [session, disqualified, cameraError, checkFace]);

  // ─── Answer handlers ────────────────────────────────────────────────────────
  const selectAnswer = async (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
    try {
      await API.post('/exam/answer', { session_id, question_id: questionId, selected_option: option });
    } catch (e) { /* silent */ }
  };

  const currentExamIsMultipleChoice = session?.multiple_choice === 1;

  const startExamFlow = async () => {
    if (requireFullscreen && !isFullscreen) {
      setError('Aktifkan mode layar penuh terlebih dahulu.');
      return;
    }
    if (cameraError) {
      setError('Kamera tidak tersedia. Pastikan izin kamera sudah diberikan.');
      return;
    }
    if (faceStatus !== 'ok') {
      setError('Wajah belum terdeteksi dengan baik. Pastikan wajah terlihat jelas di kamera.');
      return;
    }
    if (!rulesConfirmed) {
      setError('Centang persetujuan peraturan sebelum mulai ujian.');
      return;
    }

    try {
      await saveFacePhoto();
      const beginRes = await API.post('/exam/begin', { session_id });
      if (typeof beginRes?.data?.remainingSeconds === 'number') {
        setRemainingSeconds(beginRes.data.remainingSeconds);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal memulai ujian. Silakan coba lagi.');
      return;
    }

    setError('');
    setExamStarted(true);
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    clearInterval(autoSaveRef.current);
    clearInterval(faceTimerRef.current);
    // objectTimerRef sudah dinonaktifkan, tidak perlu di-clear
    try {
      await API.post('/exam/submit', { session_id });
      navigate(`/result/${session_id}`);
    } catch (err) {
      setError('Gagal mengirim jawaban: ' + (err.response?.data?.message || ''));
      setSubmitting(false);
    }
  }, [submitting, session_id, navigate]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  // ─── Render: Disqualified ────────────────────────────────────────────────────
  if (disqualified) {
    return (
      <div className="disqualified-screen">
        <div className="disq-card">
          <div className="disq-icon">🚫</div>
          <h1>Anda Didiskualifikasi</h1>
          <p>Anda telah melakukan lebih dari {maxViolations} pelanggaran.</p>
          <p>Ujian Anda dinyatakan <strong>TIDAK VALID</strong>.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">Kembali ke Dashboard</button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading-screen"><div className="spinner"></div><p>Memuat ujian...</p></div>;
  if (error) return <div className="error-screen"><p>{error}</p><button onClick={() => navigate('/dashboard')}>Kembali</button></div>;
  if (!session) return null;

  const currentQ = questions[currentIdx];
  const progress = ((currentIdx + 1) / questions.length) * 100;
  const answered = questions.filter(q => {
    const ans = answers[q.id];
    return typeof ans === 'string' && ans.trim().length > 0;
  }).length;
  const timerDanger = remainingSeconds < 300;
  const fullscreenReady = requireFullscreen ? isFullscreen : true;
  const canStartExam = fullscreenReady && !cameraError && faceStatus === 'ok' && rulesConfirmed;
  const isCurrentEssayAnswered = !!(answers[currentQ?.id] && String(answers[currentQ?.id]).trim());

  return (
    <div className="exam-page" style={{ userSelect: 'none' }}>
      {/* Warning modal */}
      {showWarningModal && (
        <div className="warning-overlay">
          <div className="warning-modal">
            <div className="warn-icon">⚠️</div>
            <h2>Peringatan Pelanggaran!</h2>
            <p>{warningDetail}</p>
            <p className="warn-count">Pelanggaran: <strong>{violationCount}</strong> / {maxViolations}</p>
            {violationCount >= maxViolations && <p className="warn-last">Pelanggaran berikutnya = DISKUALIFIKASI</p>}
          </div>
        </div>
      )}

      {/* Fullscreen prompt */}
      {requireFullscreen && !isFullscreen && (
        <div className="fullscreen-prompt">
          <div className="fs-card">
            <h2>⛶ Mode Layar Penuh Diperlukan</h2>
            <p>Ujian harus dijalankan dalam mode layar penuh</p>
            <button onClick={enterFullscreen} className="btn-primary">Aktifkan Layar Penuh</button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="exam-header">
        <div className="exam-title">{session.title}</div>
        <div className="exam-meta">
          {!requireFullscreen && <span className="violation-badge mobile-mode">📱 Mode Mobile</span>}
          <span className={`timer ${timerDanger ? 'danger' : ''}`}>⏱ {formatTime(remainingSeconds)}</span>
          <span className="progress-text">{answered}/{questions.length} dijawab</span>
          <span className={`violation-badge ${violationCount > 0 ? 'warn' : ''}`}>
            ⚠️ {violationCount}/{maxViolations}
          </span>
        </div>
      </header>

      {/* Progress bar */}
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

      {!examStarted ? (
        <div className="precheck-screen">
          <div className="precheck-card">
            <h2>Persiapan Sebelum Mulai Ujian</h2>
            <p>Pastikan semua syarat terpenuhi sebelum mengerjakan soal.</p>

            <div className="precheck-grid">
              <div className="precheck-item">
                <span className={`check-dot ${fullscreenReady ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Mode Layar Penuh</strong>
                  <p>{requireFullscreen ? (isFullscreen ? 'Aktif' : 'Belum aktif') : 'Mode mobile (opsional)'}</p>
                </div>
                {requireFullscreen && !isFullscreen && <button className="btn-mini" onClick={enterFullscreen}>Aktifkan</button>}
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${!cameraError ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Kamera</strong>
                  <p>{cameraError ? 'Kamera belum tersedia' : 'Kamera aktif'}</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${faceStatus === 'ok' ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Deteksi Wajah</strong>
                  <p>{faceStatus === 'ok' ? 'Wajah terdeteksi' : 'Wajah belum terdeteksi'}</p>
                </div>
              </div>
            </div>

            <div className="precheck-camera">
              {!cameraError ? (
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  width={220}
                  height={150}
                  playsInline
                  mirrored
                  videoConstraints={webcamVideoConstraints}
                  onUserMedia={handleUserMedia}
                  screenshotFormat="image/jpeg"
                  onUserMediaError={handleCameraError}
                  className="webcam-preview"
                />
              ) : (
                <div className="cam-error">❌ Kamera tidak tersedia</div>
              )}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>

            <div className="rules-box precheck-rules">
              <h3>⚠️ Peraturan Ujian</h3>
              <ul>
                {requireFullscreen ? (
                  <li>Ujian wajib dilakukan dalam mode layar penuh</li>
                ) : (
                  <li>Untuk perangkat mobile, mode layar penuh bersifat opsional</li>
                )}
                <li>Dilarang berpindah tab atau keluar jendela browser</li>
                <li>Kamera wajah harus aktif selama ujian</li>
                <li>Wajah harus terdeteksi selama ujian berlangsung</li>
                <li>Copy-paste dinonaktifkan selama ujian</li>
                <li>Lebih dari <strong>{maxViolations} pelanggaran</strong> akan didiskualifikasi</li>
              </ul>
            </div>

            <label className="confirm-rule">
              <input type="checkbox" checked={rulesConfirmed} onChange={(e) => setRulesConfirmed(e.target.checked)} />
              Saya sudah membaca dan menyetujui seluruh peraturan ujian.
            </label>

            <button className="btn-submit start-btn" onClick={startExamFlow} disabled={!canStartExam}>
              Mulai Kerjakan Soal
            </button>
          </div>
        </div>
      ) : (
      <div className="exam-body">
        {/* Sidebar: question nav */}
        <aside className="q-nav">
          <h3>Navigasi Soal</h3>
          <div className="q-grid">
            {questions.map((q, i) => (
              <button
                key={q.id}
                className={`q-btn ${i === currentIdx ? 'active' : ''} ${answers[q.id] ? 'answered' : ''}`}
                onClick={() => setCurrentIdx(i)}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <div className="legend">
            <span className="leg answered"></span> Dijawab
            <span className="leg active"></span> Aktif
            <span className="leg empty"></span> Belum
          </div>
          {/* Camera feed */}
          <div className="camera-box">
            <div className={`face-status ${faceStatus}`}>
              {faceStatus === 'ok' ? '✅ Wajah Terdeteksi' : '❌ Wajah Tidak Ada'}
            </div>
            {!cameraError ? (
              <Webcam
                ref={webcamRef}
                audio={false}
                width={180}
                height={130}
                playsInline
                mirrored
                videoConstraints={webcamVideoConstraints}
                onUserMedia={handleUserMedia}
                screenshotFormat="image/jpeg"
                onUserMediaError={handleCameraError}
                className="webcam-preview"
              />
            ) : (
              <div className="cam-error">❌ Kamera tidak tersedia</div>
            )}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        </aside>

        {/* Main question area */}
        <main className="q-main">
          {currentQ && (
            <div className="question-card">
              <div className="q-number">Soal {currentIdx + 1} dari {questions.length}</div>
              <p className="q-text">{currentQ.question_text}</p>
              {currentQ.photo && (
                <div className="question-photo">
                  <img src={currentQ.photo.startsWith('http') ? currentQ.photo : `/uploads/questions/${currentQ.photo}`} alt={`Soal ${currentIdx + 1}`} />
                </div>
              )}
              <div className="options">
                {currentExamIsMultipleChoice ? (
                  ['a', 'b', 'c', 'd', 'e'].map(opt => {
                    const optText = currentQ[`option_${opt}`];
                    if (!optText) return null;
                    return (
                      <button
                        key={opt}
                        className={`option-btn ${answers[currentQ.id] === opt ? 'selected' : ''}`}
                        onClick={() => selectAnswer(currentQ.id, opt)}
                      >
                        <span className="opt-label">{opt.toUpperCase()}</span>
                        <span className="opt-text">{optText}</span>
                      </button>
                    );
                  })
                ) : (
                  <div className="essay-answer-box">
                    <label>Jawaban Essay (wajib diisi)</label>
                    <textarea
                      className="essay-textarea"
                      value={answers[currentQ.id] || ''}
                      onChange={(e) => selectAnswer(currentQ.id, e.target.value)}
                      placeholder="Tulis jawaban Anda di sini..."
                      rows={8}
                    />
                  </div>
                )}
              </div>

              <div className="q-nav-btns">
                <button
                  className="btn-nav"
                  onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
                  disabled={currentIdx === 0}
                >← Sebelumnya</button>
                {currentIdx < questions.length - 1 ? (
                  <button
                    className="btn-nav btn-next"
                    onClick={() => setCurrentIdx(i => i + 1)}
                    disabled={!currentExamIsMultipleChoice && !isCurrentEssayAnswered}
                  >
                    Berikutnya →
                  </button>
                ) : (
                  <button
                    className="btn-submit"
                    onClick={handleSubmit}
                    disabled={submitting || (!currentExamIsMultipleChoice && !isCurrentEssayAnswered)}
                  >
                    {submitting ? 'Mengirim...' : '✅ Kumpulkan Ujian'}
                  </button>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
      )}
    </div>
  );
}